import { useQuery } from '@apollo/client';

import { BillingTierDocument } from '@/__generated__/graphql';

import type { BillingSubscriptionStatus, BillingTier } from '@/__generated__/graphql';

export interface BillingTierState {
  /**
   * The workspace's billing tier, or null for the dev entry tier (no
   * subscription row). `undefined` while loading or when the read failed —
   * consumers must treat "unknown" differently from "no-plan" (e.g. a tier gate
   * must not flash an upgrade prompt at a paying customer on a slow read).
   */
  readonly tier: BillingTier | null | undefined;
  /**
   * The workspace's subscription status, or null for the dev entry tier.
   * `undefined` while loading / on a failed read — the global payment-failed
   * banner (C27 leg 3) keys off `past_due`, and must not flash on an unknown.
   */
  readonly status: BillingSubscriptionStatus | null | undefined;
}

/**
 * The workspace's billing tier + status for chrome-level consumers (support
 * SLA line, feature tier gates, the global payment-failed banner). One tiny
 * normalized query — Apollo dedupes it with the BillingPage's fuller reads, so
 * extra consumers cost nothing.
 */
export function useBillingTier(): BillingTierState {
  const { data } = useQuery(BillingTierDocument);
  // `== null` covers BOTH undefined (loading) and null (errorPolicy:'all'
  // responses can resolve `data: null` — the A4/A6 partial-response lesson).
  if (data == null) {
    return { tier: undefined, status: undefined };
  }
  return {
    tier: data.billingSubscription?.tier ?? null,
    status: data.billingSubscription?.status ?? null,
  };
}
